import { create } from "zustand";
import { devtools } from "zustand/middleware";
import http from "@/lib/http";
import { toast } from "sonner";

const useAuthStore = create(
  devtools((set) => ({
    user: {
      data: {},
      loading: false,
      error: null,
    },
    loading: false,
    error: null,
    profileLoading: false,
    isAccountDeleting: false,
    isPasswordResetting: false,
    addingPhone: false,

    // Action: Login
    login: async (payload) => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.post("/auth/login", payload);
        set({ loading: false });
        toast.success(data?.message || "Logged in successfully");
        if (!data?.is2FARequired) {
          localStorage.setItem("accessToken", data?.accessToken);
        }
        return data;
      } catch (error) {
        set({ error: error?.response?.data?.error, loading: false });
        toast.error(error?.response?.data?.error || "Failed to login");
        throw error;
      }
    },

    // Action: Verify 2FA
    verify2FA: async (payload) => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.post("/auth/verify-2fa", payload);
        set({ loading: false });
        toast.success(data?.message || "2FA verified, logged in successfully");

        localStorage.setItem("accessToken", data?.accessToken);

        return data;
      } catch (error) {
        set({ loading: false, error: error?.response?.data?.error });
        toast.error(error?.response?.data?.error || "2FA verification failed");
        throw error;
      }
    },

    // Action : Resend 2FA
    resend2FA: async ({ userId }) => {
      try {
        const { data } = await http.post(`/auth/resend-2fa-otp/${userId}`);
        toast.success(data?.message || "New OTP sent to your email.");

        return data;
      } catch (error) {
        toast.error(error);
      }
    },

    // Action: Logout
    logout: async () => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.get("/auth/logout");
        set({ loading: false , user:{data:{}}});
        localStorage.removeItem("accessToken");
        toast.success(data?.message || "Logged out successfully");

        return data;
      } catch (error) {
        set({ error: "Failed to log out", loading: false });
        toast.error(error?.response?.data?.error || "Failed to logout");
        throw error;
      }
    },
    // Action: Logout
    register: async (payload) => {
      set({ loading: true, error: null });
      try {
        const { status, data = {} } = await http.post(
          "/auth/register",
          payload,
        );
        if (status === 201) {
          set({ loading: false });
          localStorage.setItem("accessToken", data?.accessToken);
          toast.success(data?.message || "Account register successfully");
        }

        return data;
      } catch (error) {
        set({ error: "Failed to register", loading: false });
        toast.error(error?.response?.data?.error || "Failed to register");
        throw error;
      }
    },

    // Action to Add Phone Number
    addPhoneNumber: async (payload) => {
      set({ addingPhone: true, error: null });
      try {
        const { data = {} } = await http.post("/auth/add-phone", payload);
        set({ addingPhone: false });
        toast.success(data?.message || "Phone number add successfully");
        return data;
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to add phone number";
        set({ error: errorMessage, addingPhone: false });
        toast.error(errorMessage);

        throw err;
      }
    },

    // Action to Update Phone Number
    updatePhoneNumber: async (payload) => {
      set({ addingPhone: true, error: null });
      try {
        const { data = {} } = await http.put("/auth/update-phone", payload);
        set({ addingPhone: false });
        toast.success(data?.message || "Phone number update successfully");
        return data;
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to update phone number";
        set({ error: errorMessage, addingPhone: false });
        toast.error(errorMessage);

        throw err;
      }
    },

    // Action to Resend Phone OTP
    resendPhoneOtp: async () => {
      set({ addingPhone: true, error: null });
      try {
        const { data = {} } = await http.post("/auth/resend-otp");
        set({ addingPhone: false });
        toast.success(data?.message || "OTP send successfully");
        return data;
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to resend OTP";
        set({ error: errorMessage, addingPhone: false });
        toast.error(errorMessage);

        throw err;
      }
    },

    // Action to Verify Phone OTP
    verifyOtp: async (payload) => {
      set({ addingPhone: true, error: null });
      try {
        const { data = {} } = await http.post("/auth/verify-otp", payload);
        set({ addingPhone: false });
        if (data?.success) {
          toast.success(data?.message || "Phone verified successfully");
        }

        return data;
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to verify otp";
        set({ error: errorMessage, addingPhone: false });
        toast.error(errorMessage);
        throw err;
      }
    },

    // Action: Complete User Registration
    completeUserRegistration: async (payload) => {
      set({ loading: true, error: null });
      try {
        const response = await http.post("/auth/profile", payload);
        set({ loading: false });
        toast.success(
          response?.data?.message || "Account register successfully",
        );

        return response?.data;
      } catch (error) {
        set({ error: "Failed to register", loading: false });
        toast.error(error?.response?.data?.error || "Failed to register");
        throw error;
      }
    },

    // Action: Complete User Registration
    veriffKYC: async () => {
      set({ loading: true, error: null });
      try {
        const response = await http.post("/auth/verify-kyc");
        set({ loading: false });
        toast.success(
          response?.data?.message || "Veriff KYC link is generated",
        );

        return response?.data;
      } catch (error) {
        set({ error: "Failed to generate link", loading: false });
        toast.error(error?.response?.data?.error || "Failed to generate link");
        throw error;
      }
    },

    // Action: Fetch User details
    getUserProfile: async () => {
      set((state) => ({
        ...state,
        user: { ...state.user, loading: true },
        error: null,
      }));
      try {
        const response = await http.get("/user/profile");
        set((state) => ({
          ...state,
          user: { ...state.user, data: response?.data, loading: false },
        }));
        return response?.data;
      } catch (error) {
        set((state) => ({
          ...state,
          user: { loading: false },
          error: error?.response?.data?.error || "Failed to fetch user",
        }));
        toast.error(error?.response?.data?.error || "Failed to fetch user");
        throw error;
      }
    },

    // Action: Update User details
    updateUserProfile: async (payload) => {
      set((state) => ({
        ...state,
        user: { ...state.user, loading: true },
        error: null,
      }));
      try {
        const response = await http.put("/user/profile", payload, {
          isMultipart: true,
        });
        toast.success(
          response?.data?.message || "Profile updated successfully",
        );
        set((state) => ({
          ...state,
          user: {
            ...state.user,
            ...response?.data,
            loading: false,
          },
          error: null,
        }));
        return response?.data;
      } catch (error) {
        set((state) => ({
          ...state,
          user: {
            ...state.user,
            loading: false,
          },
          error: error?.response?.data?.error || "Failed to update user",
        }));
        toast.error(error?.response?.data?.error || "Failed to update user");
        throw error;
      }
    },

    // Action: remove profile picture
    removeProfilePic: async () => {
      set({ profileLoading: true });
      try {
        const response = await http.post("/user/profile/remove-profile");
        toast.success(
          response?.data?.message || "Profile Pic removed successfully",
        );
        set({ profileLoading: false });

        return response?.data;
      } catch (error) {
        toast.error(
          error?.response?.data?.error || "Failed to remove profile pic",
        );
        set({ profileLoading: false });

        throw error;
      }
    },

    // Action: remove profile picture
    deleteAccount: async () => {
      set({ isAccountDeleting: true });
      try {
        const response = await http.delete("/auth/delete-account");
        toast.success(
          response?.data?.message || "Account deleted successfully",
        );
        set({ isAccountDeleting: false });

        return response?.data;
      } catch (error) {
        toast.error(error?.response?.data?.error || "Failed to delete account");
        set({ isAccountDeleting: false });

        throw error;
      }
    },

    // Action: Change password
    changePassword: async (payload) => {
      set({ isPasswordResetting: true });
      try {
        const response = await http.post("/auth/change-password", payload);
        toast.success(
          response?.data?.message || "Password changed successfully",
        );
        set({ isPasswordResetting: false });

        return response?.data;
      } catch (error) {
        toast.error(
          error?.response?.data?.error || "Failed to change password",
        );
        set({ isPasswordResetting: false });

        throw error;
      }
    },
    // Action: Forgot Password
    forgotPassword: async (email) => {
      try {
        const response = await http.post("/auth/forgot-password", email);
        toast.success(response?.data?.message || "OTP sent successfully");

        return response?.data;
      } catch (error) {
        toast.error(error?.response?.data?.error || "Failed to send OTP");

        throw error;
      }
    },
    // Action: Verify Forgot Password OTP
    verifyForgotOtp: async (payload) => {
      try {
        const response = await http.post("/auth/verify-reset-otp", payload);
        toast.success(
          response?.data?.message || "OTP is verified successfully",
        );

        return response?.data;
      } catch (error) {
        toast.error(error?.response?.data?.error || "Failed to verify OTP");

        throw error;
      }
    },
    // Action: Update password for Forgot
    updateForgotPassword: async (payload) => {
      try {
        const response = await http.post("/auth/reset-password", payload);
        toast.success(response?.data?.message || "Password reset successfully");

        return response?.data;
      } catch (error) {
        toast.error(error?.response?.data?.error || "Failed Update Password");

        throw error;
      }
    },
  })),
);

export default useAuthStore;
